import React, { useState, useEffect, useRef, useCallback } from "react";
import { Track, VideoPresets } from "livekit-client";
import { LiveKitRoom, RoomAudioRenderer, useLocalParticipant, useRoomContext, useConnectionQualityIndicator, useParticipants, useConnectionState } from "@livekit/components-react";
import { Mic, MicOff, Video, VideoOff, SwitchCamera, LayoutGrid, Headphones, PhoneOff, Loader2, Eye, ShieldAlert, Star, VolumeX } from "lucide-react";
import { toast } from "sonner";
import { usePoll } from "@/hooks/usePoll";
import { LiveStage } from "@/components/live/LiveStage";
import { LiveChat } from "@/components/live/LiveChat";
import { LiveBadge } from "@/components/live/LivePlayer";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

const LAYOUTS = [["side_by_side", "Side by side"], ["spotlight", "Spotlight"], ["solo", "Solo"]];
const QUALITY = { excellent: ["Excellent", "bg-aura-mint"], good: ["Good", "bg-aura-gold"], poor: ["Poor", "bg-red-500"], lost: ["Lost", "bg-red-500"], unknown: ["…", "bg-am-text3"] };

function useWakeLock(active) {
  useEffect(() => {
    if (!active || !navigator.wakeLock) return;
    let lock = null, dead = false;
    const grab = () => navigator.wakeLock.request("screen").then((l) => { if (dead) l.release(); else lock = l; }).catch(() => {});
    grab();
    const onVis = () => document.visibilityState === "visible" && grab();
    document.addEventListener("visibilitychange", onVis);
    return () => { dead = true; document.removeEventListener("visibilitychange", onVis); lock?.release?.(); };
  }, [active]);
}

function Elapsed({ since }) {
  const [t, setT] = useState(0);
  useEffect(() => { const i = setInterval(() => setT(Math.max(0, Math.floor((Date.now() - new Date(since).getTime()) / 1000))), 1000); return () => clearInterval(i); }, [since]);
  const h = Math.floor(t / 3600), m = Math.floor((t % 3600) / 60), s = t % 60;
  return <span data-testid="live-timer" className="tabular-nums">{h ? `${h}:` : ""}{String(m).padStart(2, "0")}:{String(s).padStart(2, "0")}</span>;
}

function Round({ onClick, icon: I, label, active, danger, testid }) {
  return <button onClick={onClick} data-testid={testid} aria-label={label} title={label} className={cn("grid h-12 w-12 place-items-center rounded-full border backdrop-blur transition-transform active:scale-90", danger ? "border-red-500 bg-red-500 text-white" : active ? "border-white bg-white text-black" : "border-white/20 bg-black/50 text-white")}><I className="h-5 w-5" /></button>;
}

function HostControls({ b, setB, onEnd }) {
  const room = useRoomContext();
  const state = useConnectionState();
  const { localParticipant, isMicrophoneEnabled, isCameraEnabled } = useLocalParticipant();
  const participants = useParticipants();
  const { quality } = useConnectionQualityIndicator({ participant: localParticipant });
  const [layoutOpen, setLayoutOpen] = useState(false);
  const [audioOnly, setAudioOnly] = useState(false);
  const [facing, setFacing] = useState("user");
  useWakeLock(true);

  const flip = async () => {
    const next = facing === "user" ? "environment" : "user";
    try { await localParticipant.setCameraEnabled(true, { facingMode: next, resolution: VideoPresets.h720.resolution }); setFacing(next); }
    catch (e) { toast("Couldn't switch camera", { description: e.message }); }
  };
  const toggleAudioOnly = async () => {
    const next = !audioOnly; setAudioOnly(next);
    await localParticipant.setCameraEnabled(!next);
    toast(next ? "Audio only — camera paused to save bandwidth" : "Camera back on");
  };
  const setLayout = async (layout) => { setLayoutOpen(false); try { setB(await api.liveLayout(b.id, { layout })); } catch (e) { toast(e.message); } };
  const spotlight = async (identity) => { try { setB(await api.liveLayout(b.id, { layout: "spotlight", spotlight: b.spotlight === identity ? "" : identity })); } catch (e) { toast(e.message); } };
  const muteOther = async (p) => {
    const pub = p.getTrackPublication(Track.Source.Microphone);
    if (!pub) return toast("No mic track to mute");
    try { await api.liveMute(b.id, p.identity, pub.trackSid); toast(`Muted ${p.name}`); } catch (e) { toast(e.message); }
  };
  const others = participants.filter((p) => p.identity !== localParticipant.identity && b.hosts.some((h) => h.user_id === p.identity));
  const [qLabel, qCls] = QUALITY[quality] || QUALITY.unknown;
  const isController = b.controller_id === localParticipant.identity;

  return (
    <>
      <div className="pointer-events-none absolute inset-x-0 top-0 flex items-center gap-2 p-3">
        <span data-testid="live-quality" className="inline-flex items-center gap-1.5 rounded-full bg-black/55 px-2.5 py-1 text-[11px] font-semibold text-white backdrop-blur"><span className={cn("h-2 w-2 rounded-full", qCls)} /> {qLabel}</span>
        {state === "reconnecting" && <span data-testid="host-reconnecting" className="rounded-full bg-aura-gold/90 px-2.5 py-1 text-[11px] font-bold text-black">Reconnecting…</span>}
        <span className="ml-auto inline-flex items-center gap-2 rounded-full bg-black/55 px-2.5 py-1 text-[11px] font-semibold text-white backdrop-blur"><Eye className="h-3.5 w-3.5" /> {b.viewers} <Elapsed since={b.started_at} /> {b.recording_active && <span className="inline-flex items-center gap-1 font-black text-red-400"><span className="h-2 w-2 animate-pulse rounded-full bg-red-500" /> REC</span>}</span>
      </div>
      {others.length > 0 && (
        <div className="absolute left-3 top-14 flex flex-col gap-1">
          {others.map((p) => (
            <div key={p.identity} className="flex items-center gap-1 rounded-full bg-black/55 px-2 py-1 text-[11px] text-white backdrop-blur">
              <span className="font-semibold">{p.name}</span>
              <button onClick={() => spotlight(p.identity)} data-testid={`spotlight-${p.identity}`} title="Spotlight" className={cn("grid h-6 w-6 place-items-center rounded-full hover:bg-white/15", b.spotlight === p.identity && "text-aura-gold")}><Star className="h-3 w-3" /></button>
              <button onClick={() => muteOther(p)} data-testid={`mute-host-${p.identity}`} title="Mute their mic" className="grid h-6 w-6 place-items-center rounded-full hover:bg-white/15"><VolumeX className="h-3 w-3" /></button>
            </div>
          ))}
        </div>
      )}
      <div className="absolute inset-x-0 bottom-0 flex flex-col items-center gap-2 p-3">
        {!isController && <span className="rounded-full bg-black/55 px-2.5 py-1 text-[10px] font-semibold text-am-text2 backdrop-blur">Co-host · {b.hosts.find((h) => h.user_id === b.controller_id)?.name || "Founder"} is controlling</span>}
        {layoutOpen && (
          <div data-testid="layout-menu" className="flex gap-1 rounded-full border border-white/15 bg-black/70 p-1 backdrop-blur">
            {LAYOUTS.map(([id, label]) => <button key={id} onClick={() => setLayout(id)} data-testid={`layout-${id}`} className={cn("rounded-full px-3 py-1.5 text-xs font-semibold", b.layout === id ? "bg-white text-black" : "text-white hover:bg-white/10")}>{label}</button>)}
          </div>
        )}
        <div className="flex items-center gap-3">
          <Round onClick={() => localParticipant.setMicrophoneEnabled(!isMicrophoneEnabled)} icon={isMicrophoneEnabled ? Mic : MicOff} label={isMicrophoneEnabled ? "Mute mic" : "Unmute mic"} active={!isMicrophoneEnabled} testid="host-mic" />
          <Round onClick={() => { setAudioOnly(false); localParticipant.setCameraEnabled(!isCameraEnabled); }} icon={isCameraEnabled ? Video : VideoOff} label={isCameraEnabled ? "Camera off" : "Camera on"} active={!isCameraEnabled} testid="host-cam" />
          <Round onClick={flip} icon={SwitchCamera} label="Flip camera" testid="host-flip" />
          <Round onClick={() => setLayoutOpen((o) => !o)} icon={LayoutGrid} label="Layout" active={layoutOpen} testid="host-layout" />
          <Round onClick={toggleAudioOnly} icon={Headphones} label="Audio only" active={audioOnly} testid="host-audio-only" />
          <Round onClick={onEnd} icon={PhoneOff} label="End broadcast" danger testid="host-end" />
        </div>
      </div>
      {room && null}
    </>
  );
}

function Preview({ onStart, mode, busy, title, setTitle, recording, setRecording, hasEvent }) {
  const videoRef = useRef(null);
  const [consent, setConsent] = useState(false);
  const [camErr, setCamErr] = useState(null);
  useEffect(() => {
    let stream;
    navigator.mediaDevices?.getUserMedia({ video: { facingMode: "user" }, audio: true }).then((s) => { stream = s; if (videoRef.current) videoRef.current.srcObject = s; }).catch((e) => setCamErr(e.message));
    return () => stream?.getTracks().forEach((t) => t.stop());
  }, []);
  return (
    <div data-testid="broadcast-preview" className="mx-auto max-w-md space-y-3">
      <div className="relative aspect-[3/4] overflow-hidden rounded-2xl bg-black sm:aspect-video">
        <video ref={videoRef} autoPlay muted playsInline className="h-full w-full object-cover [transform:scaleX(-1)]" />
        {camErr && <div className="absolute inset-0 grid place-items-center px-6 text-center text-xs text-am-text2" data-testid="preview-cam-error">Camera/mic preview unavailable: {camErr}. Allow access in your browser — you can still start and enable devices on stage.</div>}
      </div>
      {mode === "start" && !hasEvent && <input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="broadcast-title" placeholder="Broadcast title (optional)" maxLength={120} className="w-full rounded-xl border border-am-border bg-am-surface2 px-4 py-2.5 text-sm text-white outline-none focus:border-am-border-strong" />}
      {mode === "start" && (
        <label className="flex items-center gap-2 text-sm text-am-text2"><input type="checkbox" checked={recording} onChange={(e) => setRecording(e.target.checked)} data-testid="broadcast-recording" className="accent-white" /> Record this broadcast for replay</label>
      )}
      <label className="flex items-start gap-2 rounded-xl border border-aura-gold/30 bg-aura-gold/[0.06] p-3 text-xs text-am-text2">
        <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid="broadcast-consent" className="mt-0.5 accent-white" />
        <span><span className="font-semibold text-white">Consent & recording notice.</span> You will appear live on the public AuraMax Founders Channel{recording || mode === "join" ? " and this broadcast may be recorded for replay" : ""}. Keep your phone unlocked with this tab in front for the whole broadcast.</span>
      </label>
      <button onClick={onStart} disabled={!consent || busy} data-testid="broadcast-start" className="flex w-full items-center justify-center gap-2 rounded-full aura-gradient py-3 text-sm font-bold text-black disabled:opacity-40">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Video className="h-4 w-4" />} {mode === "join" ? "Join as co-host" : "Start broadcast"}
      </button>
    </div>
  );
}

// Host experience: preview + consent → publish to the LiveKit stage with phone-first controls.
export function Broadcaster({ mode = "start", existing, eventId, onLive, onEnded, onCancel }) {
  const [conn, setConn] = useState(null);
  const [b, setB] = useState(existing || null);
  const [busy, setBusy] = useState(false);
  const [title, setTitle] = useState("");
  const [recording, setRecording] = useState(true);

  const go = async () => {
    setBusy(true);
    try {
      const r = mode === "join" ? await api.liveJoin(existing.id) : await api.liveStart({ event_id: eventId || null, title, recording });
      if (mode === "join" && r.role !== "host") throw new Error("You are not a host of this broadcast.");
      setB(r.broadcast); setConn({ token: r.token, url: r.url }); onLive && onLive(r.broadcast);
    } catch (e) { toast(e.message); } finally { setBusy(false); }
  };
  const refresh = useCallback(async () => { if (!b) return; try { const nb = await api.liveBroadcast(b.id); setB(nb); if (nb.status === "ended") { setConn(null); onEnded && onEnded(nb); } } catch {} }, [b?.id, onEnded]); // eslint-disable-line react-hooks/exhaustive-deps
  usePoll(refresh, 5000, [b?.id]);
  usePoll(async () => { if (conn && b) { try { await api.livePresence(b.id); } catch {} } }, 12000, [conn, b?.id]);

  const end = async () => {
    if (!window.confirm("End the broadcast for everyone?")) return;
    try { const nb = await api.liveEnd(b.id); setConn(null); setB(nb); onEnded && onEnded(nb); toast("Broadcast ended"); } catch (e) { toast(e.message); }
  };

  if (!conn) {
    return (
      <div className="space-y-3">
        <Preview onStart={go} mode={mode} busy={busy} title={title} setTitle={setTitle} recording={recording} setRecording={setRecording} hasEvent={!!eventId} />
        <button onClick={onCancel} className="mx-auto block text-xs font-semibold text-am-text2 hover:text-white">Cancel</button>
      </div>
    );
  }
  return (
    <div data-testid="broadcaster" className="grid gap-3 lg:grid-cols-[1fr_340px]">
      <div className="relative aspect-[9/16] max-h-[82vh] w-full overflow-hidden rounded-2xl bg-black sm:aspect-video">
        <LiveKitRoom serverUrl={conn.url} token={conn.token} connect video audio
          options={{ adaptiveStream: true, dynacast: true, videoCaptureDefaults: { resolution: VideoPresets.h720.resolution, facingMode: "user" }, publishDefaults: { simulcast: true, videoSimulcastLayers: [VideoPresets.h180, VideoPresets.h360] } }}
          onError={(e) => toast("Stream error", { description: e.message })} className="h-full w-full">
          <LiveStage layout={b.layout} spotlight={b.spotlight} hostIds={b.hosts.map((h) => h.user_id)} />
          <RoomAudioRenderer />
          <HostControls b={b} setB={setB} onEnd={end} />
        </LiveKitRoom>
        <div className="pointer-events-none absolute left-1/2 top-3 -translate-x-1/2"><LiveBadge /></div>
      </div>
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2 rounded-xl border border-am-border bg-am-surface px-3 py-2 text-xs text-am-text2"><ShieldAlert className="h-3.5 w-3.5 text-aura-gold" /> Hosts: {b.hosts.map((h) => `@${h.username}`).join(", ")} · layout {b.layout.replace("_", " ")}</div>
        <LiveChat broadcast={b} canModerate onBroadcast={setB} className="h-[320px] lg:h-[calc(82vh-44px)]" />
      </div>
    </div>
  );
}
